/*
 * Single Source Suppliers — Predictive search enhancer
 * --------------------------------------------------------------------------
 * Shopify tokenises search terms on spaces, so a concatenated query such as
 * "usafety" never matches the collection "U Safety", "handtools" never matches
 * "Hand Tools", "kingtony" never matches "King Tony", and so on. This script
 * adds a space-insensitive Brands & Categories matcher to the predictive
 * dropdown: it reads a Liquid-rendered index of every collection and injects
 * the matches Shopify missed, de-duplicated against the ones it already shows.
 *
 * It enhances every <predictive-search> on the page (global header rail and the
 * search results page) and survives Shopify re-rendering the panel on each
 * keystroke via a self-detaching MutationObserver.
 */
(function () {
  'use strict';

  var idxEl = document.getElementById('ss-search-collections');
  if (!idxEl) return;

  var RAW;
  try {
    RAW = JSON.parse(idxEl.textContent);
  } catch (e) {
    return;
  }
  if (!Array.isArray(RAW) || !RAW.length) return;

  function norm(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  // Pre-compute a lookup table: title, url, brand flag, normalised keys.
  var ITEMS = [];
  for (var i = 0; i < RAW.length; i++) {
    var title = RAW[i][0];
    var handle = RAW[i][1];
    if (!title || !handle) continue;
    var isBrand = handle.lastIndexOf('brand-', 0) === 0;
    var titleKey = norm(title);
    // For brand handles, drop the "brand-" prefix so the handle key matches
    // what shoppers actually type (e.g. "usafety", not "brandusafety").
    var handleKey = norm(isBrand ? handle.slice(6) : handle);
    if (!titleKey && !handleKey) continue;
    ITEMS.push({
      title: title,
      url: '/collections/' + handle,
      isBrand: isBrand,
      titleKey: titleKey,
      handleKey: handleKey
    });
  }
  if (!ITEMS.length) return;

  var MAX_TOTAL = 8; // keep the injected block compact

  function match(term) {
    var t = norm(term);
    if (t.length < 2) return [];
    var strong = []; // title/handle starts with the term
    var weak = []; // term appears somewhere inside
    for (var j = 0; j < ITEMS.length; j++) {
      var it = ITEMS[j];
      var tk = it.titleKey;
      var hk = it.handleKey;
      if (tk.indexOf(t) === 0 || hk.indexOf(t) === 0) {
        strong.push(it);
      } else if (tk.indexOf(t) > -1 || hk.indexOf(t) > -1) {
        weak.push(it);
      }
    }
    function byRelevance(a, b) {
      // brands first, then shorter titles, then alphabetical
      if (a.isBrand !== b.isBrand) return a.isBrand ? -1 : 1;
      return a.title.length - b.title.length || a.title.localeCompare(b.title);
    }
    strong.sort(byRelevance);
    weak.sort(byRelevance);
    return strong.concat(weak);
  }

  function esc(s) {
    return (s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  var BRAND_ICON =
    '<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false"><path fill="currentColor" d="M8 1 2 3.5v4C2 11.2 4.5 14.3 8 15c3.5-.7 6-3.8 6-7.5v-4L8 1Zm0 1.6 4.4 1.8v3.1c0 2.8-1.8 5.2-4.4 5.8-2.6-.6-4.4-3-4.4-5.8V4.4L8 2.6Z"/></svg>';
  var CAT_ICON =
    '<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false"><path fill="currentColor" d="M2 2h5v5H2V2Zm7 0h5v5H9V2ZM2 9h5v5H2V9Zm7 0h5v5H9V9Z"/></svg>';

  // Collect collection URLs the native dropdown already renders, so we never
  // duplicate them.
  function existingUrls(panel) {
    var set = {};
    var links = panel.querySelectorAll('a[href*="/collections/"]');
    for (var k = 0; k < links.length; k++) {
      var p = links[k].getAttribute('href') || '';
      p = p.split('?')[0].split('#')[0].replace(/\/$/, '');
      // normalise to the trailing /collections/<handle>
      var m = p.match(/\/collections\/[^/]+$/);
      if (m) set[m[0]] = true;
    }
    return set;
  }

  function buildBlock(list, taken) {
    var brands = [];
    var cats = [];
    for (var n = 0; n < list.length && brands.length + cats.length < MAX_TOTAL; n++) {
      var it = list[n];
      if (taken['/collections/' + it.url.split('/collections/')[1]]) continue;
      (it.isBrand ? brands : cats).push(it);
    }
    if (!brands.length && !cats.length) return null;

    function group(label, arr, icon) {
      if (!arr.length) return '';
      var chips = '';
      for (var a = 0; a < arr.length; a++) {
        chips +=
          '<a class="ss-bc__chip" href="' + esc(arr[a].url) + '">' +
          icon + '<span>' + esc(arr[a].title) + '</span></a>';
      }
      return (
        '<div class="ss-bc__group">' +
        '<span class="ss-bc__label">' + label + '</span>' +
        '<div class="ss-bc__chips">' + chips + '</div>' +
        '</div>'
      );
    }

    var wrap = document.createElement('div');
    wrap.className = 'ss-bc';
    wrap.setAttribute('data-ss-bc', '');
    wrap.innerHTML =
      group('Brands', brands, BRAND_ICON) + group('Categories', cats, CAT_ICON);
    return wrap;
  }

  function enhance(root) {
    var input = root.querySelector('input[type="search"], .search__input');
    var panel = root.querySelector('[data-predictive-search]');
    if (!input || !panel) return;

    var observer = new MutationObserver(inject);

    function inject() {
      observer.disconnect();
      try {
        var old = panel.querySelector('[data-ss-bc]');
        if (old) old.parentNode.removeChild(old);

        var list = match(input.value);
        if (list.length) {
          var block = buildBlock(list, existingUrls(panel));
          if (block) panel.insertBefore(block, panel.firstChild);
        }
      } finally {
        observer.observe(panel, { childList: true, subtree: true });
      }
    }

    observer.observe(panel, { childList: true, subtree: true });
    input.addEventListener('input', function () {
      // give Shopify a tick to render, then supplement
      window.requestAnimationFrame(inject);
    });
    inject();
  }

  function init() {
    var nodes = document.querySelectorAll('predictive-search');
    for (var q = 0; q < nodes.length; q++) enhance(nodes[q]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
